import React, { useEffect, useMemo, useState, useRef } from "react";
import { api } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { Checkbox } from "../../components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "../../components/ui/popover";
import { Textarea } from "../../components/ui/textarea";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Users, Car as CarIcon, CalendarDays, Search, AlertTriangle, CheckCircle2, Layers, Plus, Send, X, Trash2 } from "lucide-react";
import html2canvas from "html2canvas";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Label } from "../../components/ui/label";
import PostponeDialog from "./PostponeDialog";
import {
  DAY_NAMES, MONTH_NAMES,
  COLORS, PROJECT_INACTIVE_CATEGORIES, DISMANTLE_WINDOW_DAYS,
  toISO, mondayOf, addDays, fmtRoDay, dowFromIso, isSaturdayIso, fmtRangeLabel,
} from "./utils";

/* eslint-disable no-unused-vars */

export default function QuickEditPopover({ card, onSaved, onCardUpdated, regii = [] }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [data, setData] = useState(null);
  const [postponeOpen, setPostponeOpen] = useState(false);
  const isRegie = card.type === "regie";

  const load = async () => {
    setLoading(true);
    try {
      const url = isRegie ? `/regii/${card.ref_id}` : `/projects/${card.ref_id}`;
      const r = await api.get(url);
      setData({
        firma: r.data.firma || "",
        adresa: r.data.adresa || "",
        start_date: r.data.start_date || "",
        end_date: r.data.end_date || "",
        indeterminate_end: !!r.data.indeterminate_end,
        min_people: r.data.min_people || r.data.default_min_people || "",
        max_people: r.data.max_people || "",
        min_cars: r.data.min_cars || r.data.default_min_cars || "",
        full: r.data,
      });
    } catch (e) { toast.error(e?.response?.data?.detail || "Eroare încărcare detalii"); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    if (open && !data && !loading) {
      load();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const save = async () => {
    if (!data) return;
    setSaving(true);
    try {
      const url = isRegie ? `/regii/${card.ref_id}` : `/projects/${card.ref_id}`;
      const minP = parseInt(data.min_people || 0) || 0;
      const minC = parseInt(data.min_cars || 0) || 0;
      // Preserve full payload to avoid wiping fields
      const payload = isRegie
        ? {
            ...data.full,
            firma: (data.firma || "").trim() || null,
            adresa: (data.adresa || "").trim() || null,
            start_date: data.start_date || null,
            end_date: data.indeterminate_end ? null : (data.end_date || null),
            indeterminate_end: !!data.indeterminate_end,
            default_min_people: minP,
            default_min_cars: minC,
          }
        : {
            ...data.full,
            firma: (data.firma || "").trim() || null,
            adresa: (data.adresa || "").trim() || null,
            start_date: data.start_date || null,
            end_date: data.end_date || null,
            min_people: minP,
            max_people: minP,
            min_cars: minC,
          };
      await api.put(url, payload);
      toast.success("Modificări salvate");
      // Optimist: actualizează imediat cardul local din CreateDayDialog pentru
      // ca badge-urile roșu/verde (👥 X/Y) să reflecte instantaneu noile valori,
      // fără a aștepta reload-ul din parent.
      onCardUpdated && onCardUpdated(card.key, {
        min_people: minP,
        max_people: isRegie ? (card.max_people || 0) : minP,
        min_cars: minC,
        firma: (data.firma || "").trim() || null,
        adresa: (data.adresa || "").trim() || null,
        start_date: data.start_date || null,
        end_date: data.indeterminate_end ? null : (data.end_date || null),
        indeterminate_end: !!data.indeterminate_end,
      });
      setOpen(false);
      onSaved && onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Eroare salvare"); }
    finally { setSaving(false); }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <span
          role="button"
          tabIndex={0}
          title="Modificare rapidă (data sfârșit, minim persoane)"
          onClick={(e) => { e.stopPropagation(); e.preventDefault(); setOpen(true); }}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") { e.stopPropagation(); e.preventDefault(); setOpen(true); }
          }}
          className="inline-flex items-center justify-center w-6 h-6 rounded text-amber-600 bg-amber-50 hover:bg-amber-500 hover:text-white transition-colors cursor-pointer"
          data-testid={`quick-edit-${card.key}`}
        >
          ⚡
        </span>
      </PopoverTrigger>
      <PopoverContent
        className="w-96 p-3"
        side="bottom"
        align="start"
        onClick={(e) => e.stopPropagation()}
        data-testid={`quick-edit-popover-${card.key}`}
      >
        <div className="text-[10px] uppercase tracking-wider font-bold text-zinc-500 mb-2">
          ⚡ Modificare rapidă · {card.label}
        </div>
        {loading || !data ? (
          <div className="text-xs text-zinc-400 italic py-3 text-center">Se încarcă…</div>
        ) : (
          <div className="space-y-2.5">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-[11px] font-semibold text-zinc-700">🏢 Firma</label>
                {(() => {
                  const DEFAULT_FIRMS = ["Implenia", "Speztec", "Klarotec", "Maba", "Imas"];
                  const fromRegii = Array.from(new Set((regii || []).map((r) => (r.firma || "").trim()).filter(Boolean)));
                  const merged = Array.from(new Set([...DEFAULT_FIRMS, ...fromRegii])).sort((a, b) => {
                    const pri = (n) => {
                      const i = DEFAULT_FIRMS.findIndex((d) => d.toLowerCase() === n.toLowerCase());
                      return i === -1 ? 99 : i;
                    };
                    const dp = pri(a) - pri(b);
                    return dp !== 0 ? dp : a.localeCompare(b);
                  });
                  const currentLower = (data.firma || "").trim().toLowerCase();
                  const isInList = merged.some((f) => f.toLowerCase() === currentLower);
                  const selValue = !data.firma ? "__none__" : (isInList ? merged.find((f) => f.toLowerCase() === currentLower) : "__custom__");
                  return (
                    <Select
                      value={selValue}
                      onValueChange={(v) => {
                        if (v === "__add__") {
                          const name = window.prompt("Nume firmă nouă:");
                          if (!name) return;
                          setData({ ...data, firma: name.trim() });
                          return;
                        }
                        if (v === "__none__") { setData({ ...data, firma: "" }); return; }
                        if (v === "__custom__") return;
                        setData({ ...data, firma: v });
                      }}
                    >
                      <SelectTrigger className="h-8 text-xs" data-testid="quick-firma-select"><SelectValue placeholder="Selectează firma" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">— Selectează —</SelectItem>
                        {merged.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}
                        {!isInList && data.firma && <SelectItem value="__custom__">📝 {data.firma} (custom)</SelectItem>}
                        <SelectItem value="__add__">➕ Adaugă firmă nouă…</SelectItem>
                      </SelectContent>
                    </Select>
                  );
                })()}
                <input type="hidden" data-testid="quick-firma" value={data.firma || ""} readOnly />
              </div>
              <div>
                <label className="text-[11px] font-semibold text-zinc-700">📍 Adresă</label>
                <Input
                  value={data.adresa}
                  onChange={(e) => setData({ ...data, adresa: e.target.value })}
                  placeholder="ex: Bahnhofstrasse 12, ZH"
                  className="h-8 text-xs"
                  data-testid="quick-adresa"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-[11px] font-semibold text-zinc-700">📅 Început</label>
                <Input
                  type="date"
                  value={data.start_date}
                  onChange={(e) => setData({ ...data, start_date: e.target.value })}
                  className="h-8 text-xs"
                  data-testid="quick-start-date"
                />
              </div>
              <div>
                <label className="text-[11px] font-semibold text-zinc-700">📅 Sfârșit</label>
                <Input
                  type="date"
                  value={data.end_date}
                  disabled={!!data.indeterminate_end}
                  onChange={(e) => setData({ ...data, end_date: e.target.value })}
                  className="h-8 text-xs"
                  data-testid="quick-end-date"
                />
                {isRegie && (
                  <label className="flex items-center gap-1.5 mt-1 text-[10px] text-zinc-700 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={!!data.indeterminate_end}
                      onChange={(e) => setData({ ...data, indeterminate_end: e.target.checked })}
                    />
                    ♾ Nedeterminată
                  </label>
                )}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-[11px] font-semibold text-zinc-700">Min persoane</label>
                <Input
                  type="number" min="0"
                  value={data.min_people}
                  onChange={(e) => setData({ ...data, min_people: e.target.value })}
                  className="h-8 text-xs tabular-nums"
                  data-testid="quick-min-people"
                />
              </div>
            </div>
            <div>
              <label className="text-[11px] font-semibold text-zinc-700">Minim mașini</label>
              <Input
                type="number" min="0"
                value={data.min_cars}
                onChange={(e) => setData({ ...data, min_cars: e.target.value })}
                className="h-8 text-xs tabular-nums"
                data-testid="quick-min-cars"
              />
            </div>
            <div className="flex items-center justify-between gap-2 pt-2 border-t border-[var(--border)]">
              <Button
                size="sm"
                variant="outline"
                onClick={(e) => { e.stopPropagation(); setPostponeOpen(true); }}
                className="h-7 text-xs border-amber-300 text-amber-700 hover:bg-amber-50"
                data-testid={`quick-postpone-${card.key}`}
                title="Amână începutul (mută start_date)"
              >
                📅 Amână
              </Button>
              <div className="flex items-center gap-2">
                <Button size="sm" variant="outline" onClick={() => setOpen(false)} className="h-7 text-xs">Anulează</Button>
                <Button size="sm" onClick={save} disabled={saving} className="h-7 text-xs bg-amber-600 hover:bg-amber-700 text-white" data-testid={`quick-save-${card.key}`}>
                  {saving ? "Salvez…" : "💾 Salvează"}
                </Button>
              </div>
            </div>
          </div>
        )}
      </PopoverContent>
      <PostponeDialog
        open={postponeOpen}
        onClose={() => setPostponeOpen(false)}
        card={card}
        currentStartDate={data?.full?.start_date || ""}
        currentEndDate={data?.full?.end_date || ""}
        currentIndeterminateEnd={!!data?.full?.indeterminate_end}
        onSaved={() => { setOpen(false); onSaved && onSaved(); }}
      />
    </Popover>
  );
}
